/**
 * Bed Builder — data catalogue + shareable links.
 *
 * 1. window.BedBuilderData — reads the JSON blob emitted by
 *    snippets/bed-builder-data.liquid (replaces the old hidden
 *    .variant-data divs).
 * 2. window.BedBuilderShare — encodes the current selection into a
 *    self-contained #b= URL hash code, restores a selection from one,
 *    and renders the Share button.
 *
 * Code layout (before Base64url):
 *   byte 0        version (1)
 *   byte 1        size index (see SIZES)
 *   bytes 2-3     presence bitmap, big-endian — bit i = LAYERS[i] filled
 *   then          6 bytes per filled layer: variant ID, big-endian,
 *                 in LAYERS order
 *
 * The code lives in the URL *hash* so link-tracking protection never
 * strips it. Never move it to a utm_* style query param.
 */
(function () {
  'use strict';

  // ---------------------------------------------------------------
  // Data catalogue
  // ---------------------------------------------------------------
  var catalogueCache = null;

  // URLs are stored as the path after "/files/" and re-joined with the
  // blob's base; anything already absolute passes through untouched.
  function absolutize(base, url) {
    if (!url) return '';
    if (url.indexOf('//') === 0 || url.indexOf('http') === 0) return url;
    return base + url;
  }

  function catalogue() {
    if (catalogueCache) return catalogueCache;
    var el = document.getElementById('bed-builder-data');
    if (!el) return {};
    var parsed;
    try {
      parsed = JSON.parse(el.textContent);
    } catch (e) {
      console.error('BedBuilderData: could not parse #bed-builder-data JSON', e);
      return (catalogueCache = {});
    }
    if (!parsed.products) {
      // v1 flat format
      return (catalogueCache = parsed.variants || {});
    }
    // v2: grouped by product — expand to the flat variant-ID map the
    // rest of the theme JS consumes.
    var base = parsed.base || '';
    var flat = {};
    parsed.products.forEach(function (p) {
      (p.v || []).forEach(function (v) {
        var layers = {};
        (v.k || []).forEach(function (key) {
          var src = (v.l && v.l[key]) || (p.l && p.l[key]) || '';
          if (src) layers[key] = absolutize(base, src);
        });
        flat[v.id] = {
          product: p.t,
          image: absolutize(base, v.i !== undefined ? v.i : p.i),
          price: v.p,
          available: !!v.a,
          layers: layers
        };
      });
    });
    return (catalogueCache = flat);
  }

  window.BedBuilderData = {
    get: function (variantId) {
      return catalogue()[String(variantId)] || null;
    },
    all: catalogue
  };

  // ---------------------------------------------------------------
  // Encoding constants — order is part of the wire format, never reorder
  // ---------------------------------------------------------------
  var VERSION = 1;
  var LAYERS = [
    'fitted-sheet',
    'quilt-cover',
    'flat-sheet',
    'euro-pillowcase',
    'pillowcase',
    'pillowcase-2',
    'feature-pillowcase',
    'feature-pillowcase-2',
    'valance'
  ];
  var LAYER_LABELS = {
    'fitted-sheet': 'Fitted Sheet',
    'quilt-cover': 'Quilt Cover',
    'flat-sheet': 'Flat Sheet',
    'euro-pillowcase': 'Euro Pillowcase',
    'pillowcase': 'Pillowcase',
    'pillowcase-2': 'Second Pillowcase',
    'feature-pillowcase': 'Feature Pillowcase',
    'feature-pillowcase-2': 'Second Feature Pillowcase',
    'valance': 'Valance'
  };
  var SIZES = [
    'Single',
    'King Single',
    'Double',
    'Queen',
    'King',
    'Super King',
    'USA King',
    'California King',
    'Cot'
  ];
  var SECOND_LAYERS = ['pillowcase-2', 'feature-pillowcase-2'];

  // ---------------------------------------------------------------
  // Base64url helpers
  // ---------------------------------------------------------------
  function bytesToBase64url(bytes) {
    var bin = '';
    for (var i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
    return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }

  function base64urlToBytes(str) {
    var b64 = str.replace(/-/g, '+').replace(/_/g, '/');
    while (b64.length % 4) b64 += '=';
    var bin = atob(b64);
    var bytes = new Uint8Array(bin.length);
    for (var i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return bytes;
  }

  // ---------------------------------------------------------------
  // Reading the current selection out of the GPO UI
  // ---------------------------------------------------------------

  // Same resolution logic as bed-builder-main.js updateOrCreateImage:
  // which layer slot does this checked swatch belong to?
  function resolveLayerName(input, entry) {
    var isSecond = input.name.indexOf('Second') !== -1;
    var isFeature = input.name.indexOf('Feature Pillowcase') !== -1;
    var keys = Object.keys(entry.layers || {});
    var matched = null;
    for (var i = 0; i < keys.length; i++) {
      var isSecondKey = SECOND_LAYERS.indexOf(keys[i]) !== -1;
      if (isSecond === isSecondKey) { matched = keys[i]; break; }
    }
    if (!matched) return null;
    if (isFeature) return isSecond ? 'feature-pillowcase-2' : 'feature-pillowcase';
    return matched;
  }

  function getCurrentSelection() {
    var selection = {}; // layer name -> { id, entry }
    var checked = document.querySelectorAll(
      'input[type="radio"][data-type="image-swatches"]:checked'
    );
    checked.forEach(function (input) {
      var variantId = input.getAttribute('gpo-data-variant-id');
      if (!variantId) return;
      if (input.value === 'Select' || input.value === '') return;
      var entry = window.BedBuilderData.get(variantId);
      if (!entry) return;
      var layer = resolveLayerName(input, entry);
      if (layer && LAYERS.indexOf(layer) !== -1) {
        selection[layer] = { id: variantId, entry: entry, value: input.value };
      }
    });

    // Bed size: the first checked non-pillowcase Size button with a known value
    var size = null;
    var sizeInputs = document.querySelectorAll(
      'input[type="radio"][data-type="buttons"][data-field-name*="Size"]:checked'
    );
    for (var i = 0; i < sizeInputs.length; i++) {
      var fieldName = sizeInputs[i].getAttribute('data-field-name') || '';
      if (fieldName.indexOf('Pillowcase') !== -1) continue;
      if (SIZES.indexOf(sizeInputs[i].value) !== -1) {
        size = sizeInputs[i].value;
        break;
      }
    }

    return { size: size, layers: selection };
  }

  // ---------------------------------------------------------------
  // Encode / decode
  // ---------------------------------------------------------------
  function encodeSelection(selection) {
    var filled = LAYERS.filter(function (l) { return selection.layers[l]; });
    if (!filled.length) return null;

    var bytes = new Uint8Array(4 + filled.length * 6);
    bytes[0] = VERSION;
    bytes[1] = Math.max(0, SIZES.indexOf(selection.size));

    var bitmap = 0;
    LAYERS.forEach(function (layer, i) {
      if (selection.layers[layer]) bitmap |= (1 << i);
    });
    bytes[2] = (bitmap >> 8) & 0xff;
    bytes[3] = bitmap & 0xff;

    var offset = 4;
    filled.forEach(function (layer) {
      var id = Number(selection.layers[layer].id);
      // 48-bit big-endian variant ID (IDs fit comfortably in 48 bits)
      var hi = Math.floor(id / 0x100000000); // top 16 bits
      var lo = id % 0x100000000;             // bottom 32 bits
      bytes[offset++] = (hi >> 8) & 0xff;
      bytes[offset++] = hi & 0xff;
      bytes[offset++] = (lo >>> 24) & 0xff;
      bytes[offset++] = (lo >>> 16) & 0xff;
      bytes[offset++] = (lo >>> 8) & 0xff;
      bytes[offset++] = lo & 0xff;
    });

    return bytesToBase64url(bytes);
  }

  function decodeCode(code) {
    var bytes;
    try {
      bytes = base64urlToBytes(code);
    } catch (e) {
      return null;
    }
    if (bytes.length < 4 || bytes[0] !== VERSION) return null;

    var size = SIZES[bytes[1]] || null;
    var bitmap = (bytes[2] << 8) | bytes[3];
    var entries = [];
    var offset = 4;
    for (var i = 0; i < LAYERS.length; i++) {
      if (!(bitmap & (1 << i))) continue;
      if (offset + 6 > bytes.length) return null; // truncated code
      var hi = (bytes[offset] << 8) | bytes[offset + 1];
      var lo = (bytes[offset + 2] * 0x1000000) +
               (bytes[offset + 3] << 16) +
               (bytes[offset + 4] << 8) +
               bytes[offset + 5];
      entries.push({ layer: LAYERS[i], id: String(hi * 0x100000000 + lo) });
      offset += 6;
    }
    return { size: size, entries: entries };
  }

  function buildShareUrl() {
    var code = encodeSelection(getCurrentSelection());
    if (!code) return null;
    return location.origin + location.pathname + '#b=' + code;
  }

  // ---------------------------------------------------------------
  // Human-readable summary of the current selection
  // ---------------------------------------------------------------
  function buildSummaryText() {
    var selection = getCurrentSelection();
    var parts = [];
    if (selection.size) parts.push('Size: ' + selection.size);
    LAYERS.forEach(function (layer) {
      var sel = selection.layers[layer];
      if (sel) parts.push(LAYER_LABELS[layer] + ': ' + (sel.value || sel.entry.product));
    });
    return parts.join(' | ');
  }

  // ---------------------------------------------------------------
  // Restore-on-load
  // ---------------------------------------------------------------
  function readCodeFromHash() {
    var m = (location.hash || '').match(/[#&]b=([A-Za-z0-9_-]+)/);
    return m ? m[1] : null;
  }

  // Longest first so 'King Single' wins over 'King', etc.
  var SIZE_TOKENS = [
    'California King', 'Super King', 'King Single', 'USA King',
    'Standard', 'Single', 'Double', 'Queen', 'King', 'Cot'
  ];

  // Find the swatch input for a (layer, variant ID) pair. The same variant
  // can appear in both the first and "Second" pillowcase fields, so we
  // filter by field flavour, not just ID.
  function findSwatchInput(layer, id) {
    var candidates = Array.prototype.slice.call(document.querySelectorAll(
      'input[type="radio"][data-type="image-swatches"][gpo-data-variant-id="' + id + '"]'
    ));
    if (!candidates.length) return null;
    var wantSecond = SECOND_LAYERS.indexOf(layer) !== -1;
    var wantFeature = layer.indexOf('feature-pillowcase') === 0;
    var exact = candidates.filter(function (input) {
      var isSecond = input.name.indexOf('Second') !== -1;
      var isFeature = input.name.indexOf('Feature Pillowcase') !== -1;
      return isSecond === wantSecond && isFeature === wantFeature;
    });
    return exact[0] || candidates[0];
  }

  function selectSize(size) {
    var buttons = document.querySelectorAll(
      'input[type="radio"][data-type="buttons"][data-field-name*="Size"]'
    );
    var applied = false;
    buttons.forEach(function (button) {
      var fieldName = button.getAttribute('data-field-name') || '';
      if (fieldName.indexOf('Pillowcase') !== -1) return;
      if (button.value === size && !button.checked && !button.disabled) {
        button.click();
        applied = true;
      }
    });
    return applied;
  }

  // Each swatch group field is size-specific ("__Quilt Cover Single",
  // "__Feature Pillowcase King", plain "__Feature Pillowcase" = Standard).
  // A group's swatches stay disabled until its category's Size buttons
  // field is set to the matching size — this derives and clicks it.
  function enableGroupForInput(input) {
    var fieldName = input.getAttribute('data-field-name') || input.name || '';
    var prefix = fieldName;
    var size = 'Standard';
    for (var i = 0; i < SIZE_TOKENS.length; i++) {
      var suffix = ' ' + SIZE_TOKENS[i];
      if (fieldName.slice(-suffix.length) === suffix) {
        prefix = fieldName.slice(0, -suffix.length);
        size = SIZE_TOKENS[i];
        break;
      }
    }
    var sizeButtons = document.querySelectorAll(
      'input[type="radio"][data-type="buttons"][data-field-name^="' + prefix + '"]'
    );
    for (var j = 0; j < sizeButtons.length; j++) {
      var sizeField = sizeButtons[j].getAttribute('data-field-name') || '';
      if (sizeField.indexOf('Size') === -1) continue;
      if (sizeButtons[j].value !== size) continue;
      if (!sizeButtons[j].checked && !sizeButtons[j].disabled) sizeButtons[j].click();
      return;
    }
  }

  // The "Second" pillowcase fields are additionally gated by checkboxes:
  // "__Add Another Pillowcase set?" and "__Feature Pillowcase Second".
  function enableSecondGate(layer) {
    var field = layer === 'feature-pillowcase-2'
      ? '__Feature Pillowcase Second'
      : '__Add Another Pillowcase set?';
    var box = document.querySelector(
      'input[type="checkbox"][data-field-name="' + field + '"]'
    );
    if (box && !box.checked && !box.disabled) box.click();
  }

  function showRestoreNotice(message) {
    var existing = document.querySelector('.bb-share-notice');
    if (existing) existing.remove();
    var notice = document.createElement('div');
    notice.className = 'bb-share-notice';
    notice.setAttribute('role', 'status');
    notice.textContent = message;
    var anchor = document.querySelector('.restart_wrapper') ||
                 document.querySelector('.gpo-app') ||
                 document.querySelector('.product-title');
    if (anchor && anchor.parentNode) {
      anchor.parentNode.insertBefore(notice, anchor);
    } else {
      document.body.appendChild(notice);
    }
    setTimeout(function () { notice.classList.add('bb-share-notice--visible'); }, 10);
  }

  function restoreSelection(decoded) {
    var pending = decoded.entries.slice();
    var unavailable = [];

    // Anything not in the catalogue any more (deleted variant / removed
    // from the collection) can never restore — drop it up front.
    pending = pending.filter(function (e) {
      if (!window.BedBuilderData.get(e.id)) {
        unavailable.push(e);
        return false;
      }
      return true;
    });

    var attempts = 0;
    var maxAttempts = 30; // 30 × 500ms = 15s worst case
    var sizeApplied = false;

    function tick() {
      attempts++;

      // Default the main category sizes to the shared bed size; each
      // pending item then enables its own group's exact size below.
      if (!sizeApplied && decoded.size) {
        sizeApplied = selectSize(decoded.size);
      }

      pending = pending.filter(function (e) {
        var input = findSwatchInput(e.layer, e.id);
        if (!input) return true; // not rendered yet — retry
        if (input.checked) return false; // restored

        if (input.disabled) {
          // Unlock the group, then retry next tick once GPO re-enables it
          if (SECOND_LAYERS.indexOf(e.layer) !== -1) enableSecondGate(e.layer);
          enableGroupForInput(input);
          return true;
        }

        input.click();
        return !input.checked; // verify — only count it restored if it stuck
      });

      if (pending.length && attempts < maxAttempts) {
        setTimeout(tick, 500);
        return;
      }

      var missed = unavailable.length + pending.length;
      if (missed > 0) {
        showRestoreNotice(
          missed === 1
            ? 'One item from this shared bed is no longer available — the rest of the look has been loaded.'
            : missed + ' items from this shared bed are no longer available — the rest of the look has been loaded.'
        );
      }
    }

    // Delay the first pass so bed-builder-main.js (which attaches its
    // swatch change listeners on the same gpoElementChanged event) is
    // initialised before we start clicking — otherwise the image layers
    // never draw for restored selections.
    setTimeout(tick, 300);
  }

  // ---------------------------------------------------------------
  // Share button
  // ---------------------------------------------------------------
  function setShareFeedback(button, message) {
    var feedback = button.querySelector('.bb-share-button__feedback');
    if (!feedback) return;
    feedback.textContent = message;
    feedback.classList.add('bb-share-button__feedback--visible');
    clearTimeout(button._bbFeedbackTimer);
    button._bbFeedbackTimer = setTimeout(function () {
      feedback.classList.remove('bb-share-button__feedback--visible');
    }, 2500);
  }

  function onShareClick(button) {
    var url = buildShareUrl();
    if (!url) {
      setShareFeedback(button, 'Select at least one item first');
      return;
    }
    // Native share sheet only on mobile, where it's actually good
    // (AirDrop/iMessage). Desktop gets a popup with the visible link.
    var isTouchMobile = window.innerWidth <= 768 && navigator.share;
    if (isTouchMobile) {
      navigator.share({ title: 'My Bed Builder look', url: url }).catch(function (err) {
        if (err && err.name !== 'AbortError') openSharePopup(url);
      });
    } else {
      openSharePopup(url);
    }
  }

  // ---------------------------------------------------------------
  // Share popup — visible link + copy button
  // ---------------------------------------------------------------
  function closeSharePopup() {
    var overlay = document.querySelector('.bb-share-popup-overlay');
    if (overlay) overlay.remove();
    document.removeEventListener('keydown', onPopupKeydown);
  }

  function onPopupKeydown(e) {
    if (e.key === 'Escape') closeSharePopup();
  }

  function openSharePopup(url) {
    closeSharePopup();

    var overlay = document.createElement('div');
    overlay.className = 'bb-share-popup-overlay';

    var popup = document.createElement('div');
    popup.className = 'bb-share-popup';
    popup.setAttribute('role', 'dialog');
    popup.setAttribute('aria-label', 'Share this bed');
    popup.innerHTML =
      '<button type="button" class="bb-share-popup__close" aria-label="Close">&times;</button>' +
      '<h6 class="bb-share-popup__title">Share your dream bed</h6>' +
      '<p class="bb-share-popup__hint">Copy the link below to save your look or send it to someone special &mdash; it opens with your bed styled exactly as you left it.</p>' +
      '<div class="bb-share-popup__row">' +
      '<input type="text" class="bb-share-popup__input" readonly>' +
      '<button type="button" class="bb-share-popup__copy">Copy</button>' +
      '</div>';

    var input = popup.querySelector('.bb-share-popup__input');
    input.value = url;

    popup.querySelector('.bb-share-popup__close').addEventListener('click', closeSharePopup);
    overlay.addEventListener('click', function (e) {
      if (e.target === overlay) closeSharePopup();
    });
    document.addEventListener('keydown', onPopupKeydown);

    input.addEventListener('focus', function () { input.select(); });

    var copyBtn = popup.querySelector('.bb-share-popup__copy');
    copyBtn.addEventListener('click', function () {
      var done = function (ok) {
        copyBtn.textContent = ok ? 'Copied!' : 'Copy failed';
        copyBtn.classList.toggle('bb-share-popup__copy--done', ok);
        setTimeout(function () {
          copyBtn.textContent = 'Copy';
          copyBtn.classList.remove('bb-share-popup__copy--done');
        }, 2000);
      };
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(url).then(
          function () { done(true); },
          function () {
            input.select();
            var ok = false;
            try { ok = document.execCommand('copy'); } catch (e) { /* noop */ }
            done(ok);
          }
        );
      } else {
        input.select();
        var ok = false;
        try { ok = document.execCommand('copy'); } catch (e) { /* noop */ }
        done(ok);
      }
    });

    overlay.appendChild(popup);
    document.body.appendChild(overlay);
    input.focus();
  }

  function copyToClipboard(button, url) {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(url).then(
        function () { setShareFeedback(button, 'Link copied!'); },
        function () { legacyCopy(button, url); }
      );
    } else {
      legacyCopy(button, url);
    }
  }

  function legacyCopy(button, url) {
    var input = document.createElement('input');
    input.value = url;
    input.style.position = 'fixed';
    input.style.opacity = '0';
    document.body.appendChild(input);
    input.select();
    var ok = false;
    try { ok = document.execCommand('copy'); } catch (e) { /* noop */ }
    input.remove();
    setShareFeedback(button, ok ? 'Link copied!' : url);
  }

  // The summary title ("YOUR BUNDLE") is rebuilt from scratch by
  // updateSummaryTable() on every selection change, so the button is a
  // single persistent element that bed-builder-sort.js re-mounts into
  // the fresh title each rebuild.
  var shareButton = null;

  function mountShareButton(container) {
    if (!container) return;
    if (!shareButton) {
      shareButton = document.createElement('button');
      shareButton.type = 'button';
      shareButton.className = 'bb-share-button';
      shareButton.innerHTML =
        '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">' +
        '<path d="M4 12v7a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-7M12 3v13M12 3 7 8m5-5 5 5" ' +
        'stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
        '<span>Share this bed</span>' +
        '<span class="bb-share-button__feedback" role="status"></span>';
      shareButton.addEventListener('click', function (e) {
        e.preventDefault();
        onShareClick(shareButton);
      });
    }
    container.appendChild(shareButton);
  }

  // ---------------------------------------------------------------
  // Calendly — "Book a consultation" CTA + popup embed
  // ---------------------------------------------------------------
  // One Calendly event per location, each linked to that store's calendar.
  // The keys match the data-location on the card's buttons.
  var CALENDLY_EVENT_URLS = {
    fitzroy: 'https://calendly.com/fitzroy-sageandclare/bed-styling-experience',
    bayswater: 'https://calendly.com/ally-sageandclare/bed-styling-experience'
  };
  // Calendly numbers custom questions a1, a2, … in form order, so aN maps
  // to the embed's question_(N-1). On both forms a4 is the question about
  // the Bed Builder design — the field reserved for the link.
  // If the questions are ever reordered in either Calendly form, this
  // number must be updated to match, or the link will land in the wrong
  // box (and the two forms must keep the link question in the same slot).
  var CALENDLY_LINK_PARAM = 'a4';
  // The visible answer above is editable by the shopper (it lives in a
  // cross-origin Calendly iframe, so we can't lock it). utm_content is a
  // second, invitee-invisible copy that always reaches the booking record
  // intact — staff can fall back to it if the field is edited or cleared.
  var CALENDLY_TRACKING_PARAM = 'utm_content';

  function buildCalendlyUrl(location) {
    var url = CALENDLY_EVENT_URLS[location] || CALENDLY_EVENT_URLS.bayswater;
    var shareUrl = buildShareUrl();
    if (shareUrl) {
      url += (url.indexOf('?') === -1 ? '?' : '&') +
        CALENDLY_LINK_PARAM + '=' + encodeURIComponent(shareUrl) +
        '&' + CALENDLY_TRACKING_PARAM + '=' + encodeURIComponent(shareUrl);
    }
    return url;
  }

  // The widget is never fetched during page load — it's warmed up on idle
  // (or the moment the shopper shows intent by hovering/touching the
  // button), so by click time the popup opens straight onto Calendly's own
  // white card instead of showing Calendly's blue dot spinner on the dimmed
  // page first, then a second in-iframe loader.
  var calendlyLoading = false;   // widget.js request in flight
  var calendlyRequested = false; // warm-up already kicked off
  var calendlyPending = null;    // location clicked while it was still loading

  function loadCalendly() {
    if (calendlyRequested) return;
    calendlyRequested = true;
    calendlyLoading = true;

    var css = document.createElement('link');
    css.rel = 'stylesheet';
    css.href = 'https://assets.calendly.com/assets/external/widget.css';
    document.head.appendChild(css);

    var script = document.createElement('script');
    script.src = 'https://assets.calendly.com/assets/external/widget.js';
    script.async = true;
    script.onload = function () {
      calendlyLoading = false;
      if (!calendlyPending) return; // warm-up only — nothing to open yet
      var location = calendlyPending;
      calendlyPending = null;
      if (window.Calendly && window.Calendly.initPopupWidget) {
        window.Calendly.initPopupWidget({ url: buildCalendlyUrl(location) });
      } else {
        window.open(buildCalendlyUrl(location), '_blank', 'noopener');
      }
    };
    script.onerror = function () {
      calendlyLoading = false;
      calendlyRequested = false; // let a later click retry
      if (calendlyPending) {
        var location = calendlyPending;
        calendlyPending = null;
        window.open(buildCalendlyUrl(location), '_blank', 'noopener');
      }
    };
    document.head.appendChild(script);
  }

  function openCalendly(location) {
    if (window.Calendly && window.Calendly.initPopupWidget) {
      window.Calendly.initPopupWidget({ url: buildCalendlyUrl(location) });
      return;
    }
    // Not warm yet — remember the intent and open as soon as it lands.
    calendlyPending = location;
    if (!calendlyLoading) loadCalendly();
  }

  function insertConsultCta() {
    if (document.querySelector('.bb-consult')) return;
    var anchor = document.querySelector('.restart_wrapper');
    if (!anchor || !anchor.parentNode) return;

    var card = document.createElement('div');
    card.className = 'bb-consult';
    card.innerHTML =
      '<h6 class="bb-consult__title">Book a Bed Styling Experience</h6>' +
      '<p class="bb-consult__text">Want to see your selection on a real bed or play with options? ' +
      'Book a complimentary bed styling experience at one of our Melbourne locations.</p>' +
      '<p class="bb-consult__text">The details of the bed you&rsquo;ve designed here will be attached to your booking automatically. ' +
      'When you arrive for your appointment, it will be ready for you to see on the bed.</p>' +
      '<p class="bb-consult__text">We look forward to welcoming you into our design space.</p>' +
      '<div class="bb-consult__buttons">' +
      '<button type="button" class="bb-consult__button" data-location="fitzroy">Book in Fitzroy</button>' +
      '<button type="button" class="bb-consult__button" data-location="bayswater">Book in Bayswater</button>' +
      '</div>';
    Array.prototype.forEach.call(card.querySelectorAll('.bb-consult__button'), function (button) {
      button.addEventListener('click', function (e) {
        e.preventDefault();
        openCalendly(button.getAttribute('data-location'));
      });
      // Intent signals — by the time the click lands the widget is usually
      // already parsed, so the popup opens straight onto Calendly's card.
      button.addEventListener('mouseenter', loadCalendly);
      button.addEventListener('touchstart', loadCalendly, { passive: true });
      button.addEventListener('focus', loadCalendly);
    });

    anchor.parentNode.insertBefore(card, anchor.nextSibling);

    // Belt and braces for anyone who clicks without hovering first (most
    // touch users): warm up once the page has gone quiet.
    if (window.requestIdleCallback) {
      requestIdleCallback(loadCalendly, { timeout: 6000 });
    } else {
      setTimeout(loadCalendly, 4000);
    }
  }

  // ---------------------------------------------------------------
  // Keep the address bar in sync with the current selection
  // ---------------------------------------------------------------
  // A #b= code arriving via a shared link would otherwise go stale as
  // soon as the shopper changes anything — someone copying the URL bar
  // would share the OLD bed. Instead the hash is rewritten (debounced)
  // after every selection change, so the address bar is always a valid
  // share link; with nothing selected the hash is removed entirely.
  var hashTimer = null;

  function refreshHash() {
    clearTimeout(hashTimer);
    hashTimer = setTimeout(function () {
      var code = encodeSelection(getCurrentSelection());
      var base = location.pathname + location.search;
      try {
        history.replaceState(null, '', code ? base + '#b=' + code : base);
      } catch (e) { /* some browsers rate-limit replaceState — harmless */ }
    }, 300);
  }

  document.addEventListener('change', function (e) {
    if (!e.target || !e.target.matches) return;
    if (e.target.matches('input[data-type="image-swatches"], input[data-type="buttons"], .gpo-app input[type="checkbox"]')) {
      refreshHash();
    }
  });

  // "Start Over" reloads the page — strip the hash first so the old bed
  // isn't immediately restored again.
  document.addEventListener('click', function (e) {
    if (e.target.closest && e.target.closest('.restart_wrapper .restart')) {
      try { history.replaceState(null, '', location.pathname + location.search); } catch (err) { /* noop */ }
    }
  }, true);

  // ---------------------------------------------------------------
  // Boot
  // ---------------------------------------------------------------
  var booted = false;
  function boot() {
    if (booted) return;
    booted = true;
    insertConsultCta();
    var code = readCodeFromHash();
    if (code) {
      var decoded = decodeCode(code);
      if (decoded && decoded.entries.length) {
        restoreSelection(decoded);
      }
    }
  }

  // GPO renders asynchronously — bed-builder-sort.js fires
  // gpoElementChanged once .gpo-app appears. Also poll as a fallback in
  // case the event fired before this script attached its listener.
  document.addEventListener('gpoElementChanged', boot);
  var bootPoll = setInterval(function () {
    if (booted) { clearInterval(bootPoll); return; }
    if (document.querySelector('input[type="radio"][gpo-data-variant-id]')) boot();
  }, 500);
  setTimeout(function () { clearInterval(bootPoll); }, 30000);

  // Public API
  window.BedBuilderShare = {
    buildShareUrl: buildShareUrl,
    encodeSelection: encodeSelection,
    decodeCode: decodeCode,
    getCurrentSelection: getCurrentSelection,
    buildSummaryText: buildSummaryText,
    mountShareButton: mountShareButton,
    LAYERS: LAYERS,
    SIZES: SIZES
  };
})();
